/**
 * SYM-6351, scene 14 (D24, D27, M-9, P-4). The dates on a row of the staff "Ad history" panel.
 *
 * The server decides which version each day counts for (the 15:00 rule in the account's time zone,
 * AdVersionHistory.php) and sends plain days; this only writes them the way the panel shows them:
 * "3-9 Oct", "28 Sep-3 Oct", "from 10 Oct".
 */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** "2026-10-03" -> { day: 3, month: "Oct" }. Parsed by hand: a Date would shift it by time zone. */
const parts = (isoDay: string) => {
  const [, m, d] = isoDay.split('-').map((p) => parseInt(p, 10));
  return { day: d, month: MONTHS[m - 1] };
};

export const formatDay = (isoDay: string): string => {
  const { day, month } = parts(isoDay);
  return `${day} ${month}`;
};

export interface VersionRow {
  current: boolean;
  first_day: string | null;
  last_day: string | null;
  delivered: boolean;
}

export const versionDates = (row: VersionRow): string => {
  if (!row.delivered && !row.current) return 'update not delivered';
  if (row.current) {
    return row.first_day
      ? `from ${formatDay(row.first_day)}`
      : 'waiting for the update';
  }
  if (!row.first_day || !row.last_day) return '';
  // Replaced on the day it went live, at or before 15:00: that day counts for the next version,
  // so this one has no full day of its own (test book 8.3, "28-27 Sep", 28 Sep 2026).
  if (row.last_day < row.first_day) {
    return `${formatDay(row.first_day)}, replaced the same day`;
  }

  const first = parts(row.first_day);
  const last = parts(row.last_day);
  if (row.first_day === row.last_day) return formatDay(row.first_day);
  if (first.month === last.month)
    return `${first.day}-${last.day} ${last.month}`;
  return `${formatDay(row.first_day)}-${formatDay(row.last_day)}`;
};
